import { createTheme, type Theme } from '@mui/material/styles';
import { enUS as coreEnUS, nlNL as coreNlNL } from '@mui/material/locale';
import { enUS as dataGridEnUS, nlNL as dataGridNlNL } from '@mui/x-data-grid/locales';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { appBarEnvironmentColor } from '../config/appEnvironment';

// Microsoft Fluent-inspired palette.
const fluentBlue = '#0f6cbd';

/**
 * Builds the MUI theme (Fluent-inspired palette and component overrides) for
 * the given color mode and language.
 * @param mode - 'light' or 'dark' color scheme.
 * @param language - Active i18next language code; selects MUI component translations.
 * @returns A configured MUI {@link Theme}.
 */
export function buildTheme(mode: 'light' | 'dark', language: string): Theme {
  return createTheme({
    palette: {
      mode,
      primary: {
        main: fluentBlue,
      },
      secondary: {
        main: '#5c2e91',
      },
      background:
        mode === 'light'
          ? { default: '#f5f5f5', paper: '#ffffff' }
          : { default: '#1b1a19', paper: '#252423' },
    },
    shape: {
      borderRadius: 8,
    },
    typography: {
      fontFamily:
        '"Segoe UI", "Segoe UI Web (West European)", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'default' },
        styleOverrides: {
          root: {
            borderBottom: '1px solid rgba(128,128,128,0.2)',
            backgroundColor: appBarEnvironmentColor(mode),
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
      },
      MuiDataGrid: {
        styleOverrides: {
          columnHeader: {
            display: 'flex',
            alignItems: 'center',
          },
          columnHeaderTitleContainer: {
            alignItems: 'center',
          },
          columnHeaderTitle: {
            fontWeight: 600,
            lineHeight: 1.3,
          },
          cell: {
            display: 'flex',
            alignItems: 'center',
            lineHeight: 'normal',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            verticalAlign: 'middle',
          },
          head: {
            fontWeight: 600,
          },
        },
      },
    },
  }, ...componentLocales(language));
}

/**
 * Built-in MUI translations (pagination, grid toolbar, filters, etc.) for the
 * active UI language. Dutch is the default, matching the i18n setup.
 * @param language - Active i18next language code.
 */
function componentLocales(language: string) {
  return language.startsWith('en') ? [coreEnUS, dataGridEnUS] : [coreNlNL, dataGridNlNL];
}
